import { AxiosError } from 'axios';
import { ApiService } from '../../api/api-service';

const SERVICE_ROOT = '/systemsettings';

// Type definitions matching backend DTOs
export interface DueDatesDto {
  manufacturerId: number;
  leadTime: number;
  deliveryWindow: number;
  maxLinearFeet: number;
}

export interface NotificationsDto {
  manufacturerId: number;
  emailList: string;
}

export interface EngineeringFeesDto {
  manufacturerId: number;
  openPoleBarnFee: number;
  partialClosedPoleBarnFee: number;
  fullyEnclosedPoleBarnFee: number;
  permanentDwellingFee: number;
}

export interface PaintSettingsDto {
  manufacturerId: number;
  surfaceAreaPerCoveredPerGallon: number;
  surfaceAreaPerLinearFoot: number;
  markupPercentage: number;
  workHoursPerMonth: number;
  utilityCostList: string[];
  packagingCostList: string[];
}

/**
 * Service for system settings-related API operations
 */
export class SystemSettingsService extends ApiService {
  /**
   * Get due dates settings
   * @param manufacturerId Optional manufacturer ID (if not provided, uses token manufacturer)
   * @returns Promise resolving to due dates settings, or null if settings don't exist (404)
   * @throws Error for other HTTP status codes (including 500 for server errors)
   */
  async getDueDatesSettings(manufacturerId?: number): Promise<DueDatesDto | null> {
    const url = manufacturerId
      ? `${SERVICE_ROOT}/duedates/${manufacturerId}`
      : `${SERVICE_ROOT}/duedates`;
    try {
      return await this.client.get(url);
    } catch (error: unknown) {
      // If settings don't exist (404), return null instead of throwing
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      // For all other errors (including 500), re-throw to use standard error handling
      throw error;
    }
  }

  /**
   * Update due dates settings
   * @param manufacturerId Optional manufacturer ID (if not provided, uses token manufacturer)
   * @param dueDates Due dates settings data
   * @returns Promise resolving when update is complete
   */
  async updateDueDatesSettings(
    manufacturerId: number | undefined,
    dueDates: Omit<DueDatesDto, 'manufacturerId'>
  ): Promise<boolean> {
    const url = manufacturerId
      ? `${SERVICE_ROOT}/duedates/${manufacturerId}`
      : `${SERVICE_ROOT}/duedates`;
    return this.handleApiCall(
      () => this.client.put(url, dueDates),
      'Error updating due dates settings'
    );
  }

  /**
   * Get notification settings
   * @param manufacturerId Optional manufacturer ID (if not provided, uses token manufacturer)
   * @returns Promise resolving to notification settings, or null if settings don't exist (404)
   * @throws Error for other HTTP status codes (including 500 for server errors)
   */
  async getNotificationSettings(manufacturerId?: number): Promise<NotificationsDto | null> {
    const url = manufacturerId
      ? `${SERVICE_ROOT}/notifications/${manufacturerId}`
      : `${SERVICE_ROOT}/notifications`;
    try {
      return await this.client.get(url);
    } catch (error: unknown) {
      // If settings don't exist (404), return null instead of throwing
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      // For all other errors (including 500), re-throw to use standard error handling
      throw error;
    }
  }

  /**
   * Update notification settings
   * @param manufacturerId Optional manufacturer ID (if not provided, uses token manufacturer)
   * @param notifications Notification settings data
   * @returns Promise resolving when update is complete
   */
  async updateNotificationSettings(
    manufacturerId: number | undefined,
    notifications: Omit<NotificationsDto, 'manufacturerId'>
  ): Promise<boolean> {
    const url = manufacturerId
      ? `${SERVICE_ROOT}/notifications/${manufacturerId}`
      : `${SERVICE_ROOT}/notifications`;
    return this.handleApiCall(
      () => this.client.put(url, notifications),
      'Error updating notification settings'
    );
  }

  /**
   * Get engineering fees settings
   * @param manufacturerId Optional manufacturer ID (if not provided, uses token manufacturer)
   * @returns Promise resolving to engineering fees settings, or null if settings don't exist (404)
   * @throws Error for other HTTP status codes (including 500 for server errors)
   */
  async getEngineeringFeesSettings(manufacturerId?: number): Promise<EngineeringFeesDto | null> {
    const url = manufacturerId
      ? `${SERVICE_ROOT}/engineeringfees/${manufacturerId}`
      : `${SERVICE_ROOT}/engineeringfees`;
    try {
      return await this.client.get(url);
    } catch (error: unknown) {
      // If settings don't exist (404), return null instead of throwing
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      // For all other errors (including 500), re-throw to use standard error handling
      throw error;
    }
  }

  /**
   * Update engineering fees settings
   * @param manufacturerId Optional manufacturer ID (if not provided, uses token manufacturer)
   * @param engineeringFees Engineering fees settings data
   * @returns Promise resolving when update is complete
   */
  async updateEngineeringFeesSettings(
    manufacturerId: number | undefined,
    engineeringFees: Omit<EngineeringFeesDto, 'manufacturerId'>
  ): Promise<boolean> {
    const url = manufacturerId
      ? `${SERVICE_ROOT}/engineeringfees/${manufacturerId}`
      : `${SERVICE_ROOT}/engineeringfees`;
    return this.handleApiCall(
      () => this.client.put(url, engineeringFees),
      'Error updating engineering fees settings'
    );
  }

  /**
   * Get paint configuration settings
   * @param manufacturerId Optional manufacturer ID (if not provided, uses token manufacturer)
   * @returns Promise resolving to paint configuration settings, or null if settings don't exist (404)
   * @throws Error for other HTTP status codes (including 500 for server errors)
   */
  async getPaintConfigurationSettings(manufacturerId?: number): Promise<PaintSettingsDto | null> {
    const url = manufacturerId
      ? `${SERVICE_ROOT}/paintconfig/${manufacturerId}`
      : `${SERVICE_ROOT}/paintconfig`;
    try {
      return await this.client.get(url);
    } catch (error: unknown) {
      // If settings don't exist (404), return null instead of throwing
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      // For all other errors (including 500), re-throw to use standard error handling
      throw error;
    }
  }

  /**
   * Update paint configuration settings
   * @param manufacturerId Optional manufacturer ID (if not provided, uses token manufacturer)
   * @param paintSettings Paint configuration settings data
   * @returns Promise resolving when update is complete
   */
  async updatePaintConfigurationSettings(
    manufacturerId: number | undefined,
    paintSettings: Omit<PaintSettingsDto, 'manufacturerId'>
  ): Promise<boolean> {
    const url = manufacturerId
      ? `${SERVICE_ROOT}/paintconfig/${manufacturerId}`
      : `${SERVICE_ROOT}/paintconfig`;
    return this.handleApiCall(
      () => this.client.put(url, paintSettings),
      'Error updating paint configuration settings'
    );
  }
}
